import React from "react"
import { createBrowserRouter, RouterProvider, RouteObject } from "react-router-dom"
import { I18nextProvider } from "react-i18next"
import { AnimatePresence } from "framer-motion"
import { ThemeProvider } from "@mui/material/styles"
import theme from "lib/material-ui/theme"
import i18n from "lib/i18n"
import { PATH_INFOS } from "utils/navigatorUtils"
import { AUTH_LAYOUT } from "constants/index"
import { PUBLIC_PAGES } from "constants/pages"
import RtlProvider from "context/RtlProvider"
import StoreProvider from "context/StoreProvider"
import { AuthProvider } from "context/AuthContext"
import { PushNotificationProvider } from "hooks/usePushSubscription"
import MainLayout from "components/templates/MainLayout"
import PrivateRoute from "components/templates/PrivateRoute"
import AuthLayout from "components/templates/AuthLayout"
import RootLayout from "components/templates/RootLayout"
import ErrorTemplate from "components/templates/ErrorTemplate"
import AppRootWrapper from "components/templates/AppRootWrapper"

export default function Providers() {
  function createRoutesFromPagesInfo() {
    const protectedRoutes = Object.values(PATH_INFOS).map((path) => {
      const element = path.layoutType === AUTH_LAYOUT
        ? getComponentInAuthLayout(path.component)
        : getComponentInMainLayout(path.component, path.isProtected)

      return {
        path: path.path,
        key: path.key,
        element
      }
    })

    const publicRoutes = PUBLIC_PAGES.map((page) => {
      const publicPath = `/public${page.path}`
      const element = getComponentInMainLayout(page.component, false)

      return {
        path: publicPath,
        key: `public-${page.key}`,
        element
      }
    })

    return [...protectedRoutes, ...publicRoutes]
  }

  function getComponentInAuthLayout(component: React.ReactNode) {
    return (
      <AuthLayout>
        {component}
      </AuthLayout>
    )
  }

  function getComponentInMainLayout(component: React.ReactNode, isProtected: boolean) {
    return (
      isProtected ? <PrivateRoute>
        <MainLayout>{component}</MainLayout>
      </PrivateRoute> : <MainLayout>{component}</MainLayout>
    )
  }

  const routes: RouteObject[] = [
    {
      element: <PushNotificationProvider>
        <AppRootWrapper>
          <RootLayout/>
        </AppRootWrapper>
      </PushNotificationProvider>,
      children: [
        {
          path: "*",
          element: <ErrorTemplate/>
        },
        ...createRoutesFromPagesInfo()
      ]
    }
  ]

  const router = createBrowserRouter(routes)

  return (
    <StoreProvider>
      <AuthProvider>
        <I18nextProvider i18n={i18n}>
          <RtlProvider>
            <ThemeProvider theme={theme}>
              <AnimatePresence>
                <RouterProvider router={router}/>
              </AnimatePresence>
            </ThemeProvider>
          </RtlProvider>
        </I18nextProvider>
      </AuthProvider>
    </StoreProvider>
  )
}
